import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranch } from "../context/BranchContext";
import { PageHeader, fmtMin } from "../components/shared";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, ClipboardCheck } from "lucide-react";

const STATUS_COLORS = {
  "Approved": "bg-green-900/40 text-green-300 border-green-800",
  "Pending Approval": "bg-yellow-900/40 text-yellow-300 border-yellow-800",
  "Rejected": "bg-red-900/40 text-red-300 border-red-800",
  "Missing Check-out": "bg-orange-900/40 text-orange-300 border-orange-800",
  "Manual Correction": "bg-blue-900/40 text-blue-300 border-blue-800",
};

const timeOnly = (iso) => iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—";

export default function Attendance() {
  const { user } = useAuth();
  const { branchParam } = useBranch();
  const [rows, setRows] = useState([]);
  const [approve, setApprove] = useState(null);
  const [form, setForm] = useState({ reg: 0, extra: 0, status: "Approved", remarks: "", reason: "" });
  const isManager = ["OWNER", "SUPER_ADMIN"].includes(user?.role) || !!(user?.permissions || {}).attendance_edit;

  const load = () => api.get("/attendance", { params: { branch_id: branchParam } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, [branchParam]);

  const openApprove = (row) => {
    setForm({
      reg: row.raw_minutes || row.approved_regular_minutes || 0,
      extra: row.approved_extra_minutes || 0,
      status: "Approved", remarks: row.manager_remarks || "", reason: "",
    });
    setApprove(row);
  };

  const submitApprove = async () => {
    try {
      await api.put(`/attendance/${approve.id}/approve`, {
        approved_regular_minutes: Number(form.reg),
        approved_extra_minutes: Number(form.extra),
        status: form.status, manager_remarks: form.remarks, reason: form.reason,
      });
      toast.success("Attendance updated");
      setApprove(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Attendance Dashboard"
        subtitle="Biometric raw data → manager approval → approved working minutes" />

      <Card className="bg-card border-border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-transparent">
              <TableHead>Employee</TableHead>
              <TableHead>Branch</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Check-in</TableHead>
              <TableHead>Check-out</TableHead>
              <TableHead className="text-right">Raw</TableHead>
              <TableHead className="text-right">Approved</TableHead>
              <TableHead className="text-right">Extra</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Remarks</TableHead>
              {isManager && <TableHead></TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} className="border-border" data-testid={`attendance-row-${r.id}`}>
                <TableCell className="font-medium">{r.staff_name}</TableCell>
                <TableCell className="text-muted-foreground text-xs">{r.branch_name?.split("–").pop().trim()}</TableCell>
                <TableCell>{r.date}</TableCell>
                <TableCell>{timeOnly(r.check_in)}</TableCell>
                <TableCell>{timeOnly(r.check_out)}</TableCell>
                <TableCell className="text-right">{r.raw_minutes}</TableCell>
                <TableCell className="text-right text-primary font-medium">{r.approved_regular_minutes}</TableCell>
                <TableCell className="text-right">{r.approved_extra_minutes}</TableCell>
                <TableCell><Badge variant="outline" className={STATUS_COLORS[r.status] || ""}>{r.status}</Badge></TableCell>
                <TableCell className="text-muted-foreground text-xs max-w-[140px] truncate">{r.manager_remarks}</TableCell>
                {isManager && (
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => openApprove(r)}
                      data-testid={`approve-btn-${r.id}`} className="text-primary h-8">
                      <ClipboardCheck size={15} className="mr-1" /> Review
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-10">No attendance records.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!approve} onOpenChange={(o) => !o && setApprove(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Review Working Minutes</DialogTitle>
          </DialogHeader>
          {approve && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm bg-secondary p-3 rounded-sm">
                <div><span className="text-muted-foreground">Employee</span><p className="font-medium">{approve.staff_name}</p></div>
                <div><span className="text-muted-foreground">Raw Biometric</span><p className="font-medium">{fmtMin(approve.raw_minutes)}</p></div>
                <div><span className="text-muted-foreground">Check-in</span><p>{timeOnly(approve.check_in)}</p></div>
                <div><span className="text-muted-foreground">Check-out</span><p>{timeOnly(approve.check_out)}</p></div>
              </div>
              <p className="text-xs text-muted-foreground">Approve only the genuine working minutes. If staff forgot to check out, correct the minutes here — raw data is never auto-trusted.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Approved Regular Minutes</Label>
                  <Input type="number" data-testid="approve-reg-minutes" value={form.reg}
                    onChange={(e) => setForm({ ...form, reg: e.target.value })} className="mt-1.5 bg-secondary" />
                </div>
                <div>
                  <Label>Approved Extra / OT Minutes</Label>
                  <Input type="number" data-testid="approve-extra-minutes" value={form.extra}
                    onChange={(e) => setForm({ ...form, extra: e.target.value })} className="mt-1.5 bg-secondary" />
                </div>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5 bg-secondary" data-testid="approve-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Manual Correction">Manual Correction</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Manager Remarks</Label>
                <Input data-testid="approve-remarks" value={form.remarks}
                  onChange={(e) => setForm({ ...form, remarks: e.target.value })} className="mt-1.5 bg-secondary" />
              </div>
              <div>
                <Label>Reason for change (audit)</Label>
                <Textarea data-testid="approve-reason" value={form.reason}
                  onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5 bg-secondary" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={submitApprove} data-testid="approve-submit"
              className="rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37]">
              <CheckCircle2 size={16} className="mr-1" /> Save & Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
